// Esto se hace para poder mockear las funciones que se llaman dentro de otras funciones
// que están en el mismo módulo.
// @see https://stackoverflow.com/a/55193363
import * as CommonUtils from '.';

export const range = (start: number, end = 0): number[] => {
	const length = end - start + 1;
	return Array.from({ length }, (_, index) => index + start);
};

export const clamp = (value: number, min: number, max: number) => {
	return Math.min(Math.max(value, min), max);
};

export const getSignificantDecimals = (n: number) => {
	const a = Math.abs(n);
	let c = a,
		count = 1;
	while (!Number.isInteger(c) && isFinite(c)) {
		c = a * Math.pow(10, count++);
	}
	return count - 1;
};

export function quantile(arr: number[], q: number): number {
	const sorted = arr.slice().sort((n1, n2) => n1 - n2);
	const pos = (sorted.length - 1) * q;
	const base = Math.floor(pos);
	const rest = pos - base;
	if (sorted[base + 1] !== undefined) {
		return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
	} else {
		return sorted[base];
	}
}

export function minMax(arr: number[]): [number, number] {
	return arr.reduce(
		(mm: [number, number], val: number) => {
			mm[0] = mm[0] === undefined || val < mm[0] ? val : mm[0];
			mm[1] = mm[1] === undefined || val > mm[1] ? val : mm[1];
			return mm;
		},
		[Number.MAX_SAFE_INTEGER, Number.MIN_SAFE_INTEGER]
	);
}

export const getPosition = ({
	value,
	min,
	max,
}: {
	value: number;
	min: number;
	max: number;
}) => {
	const position = ((value - min) / (max - min)) * 100;
	return Math.min(Math.max(position, 0), 100);
};

export const assert = (
	condition: boolean,
	message = 'Assertion failed'
): void => {
	if (!condition) {
		throw new Error(message);
	}
};

export const mergeRefs = (...refs: any[]) => {
	const filteredRefs = refs.filter(Boolean);
	if (!filteredRefs.length) return null;
	if (filteredRefs.length === 1) return filteredRefs[0];
	return (inst: any) => {
		for (const ref of filteredRefs) {
			if (typeof ref === 'function') {
				ref(inst);
			} else if (ref) {
				ref.current = inst;
			}
		}
	};
};

export const debounce = <T extends (...args: any[]) => any>(
	callback: T,
	waitFor: number
) => {
	let timeout: any = 0;
	return (...args: Parameters<T>): ReturnType<T> => {
		let result: any;
		clearTimeout(timeout);
		timeout = setTimeout(() => {
			result = callback(...args);
		}, waitFor);
		return result;
	};
};

export const downloadBlob = (filename: string, blob: Blob) => {
	const url = window.URL.createObjectURL(blob);
	CommonUtils.triggerLinkClick(url, filename);
};

export const urlToBlob = async (url: string): Promise<Blob> => {
	const response = await fetch(url);
	if (response.body?.constructor.name === 'Blob') {
		return await response.blob();
	}

	if (response.body?.constructor.name === 'ReadableStream') {
		const stream: ArrayBuffer = await response.arrayBuffer();
		return new Blob([new Uint8Array(stream, 0, stream.byteLength)]);
	}

	const text = await response.text();
	return new Blob([text]);
};

export const triggerLinkClick = (url: string, filename: string): void => {
	const a = document.createElement('a');
	a.href = url;
	a.download = `${filename}`;
	a.target = '_blank';
	document.body.appendChild(a);
	a.click();
	a.remove();
};

export const wait = (time: number): Promise<void> => {
	return new Promise((res) => {
		const tm = setTimeout(() => {
			res();
			clearTimeout(tm);
		}, time);
	});
};

export const strContains = (
	target: string,
	crit: string,
	ignoreCase = true
): boolean => {
	if (ignoreCase) {
		return target.toLocaleLowerCase().includes(crit.toLocaleLowerCase());
	} else {
		return target.includes(crit);
	}
};

export const arrayMoveMutate = <T>(
	array: T[],
	from: number,
	to: number
): void => {
	const startIndex = from < 0 ? array.length + from : from;

	if (startIndex >= 0 && startIndex < array.length) {
		const endIndex = to < 0 ? array.length + to : to;

		const [item] = array.splice(from, 1);
		array.splice(endIndex, 0, item);
	}
};

export const arrayMove = <T>(array: T[], from: number, to: number): T[] => {
	array = [...array];
	arrayMoveMutate(array, from, to);
	return array;
};

export const createId = (prefix = '', suffix = '') => {
	const rand = Math.random().toString(36).slice(2, 11);

	return `${prefix}${rand}${suffix}`;
};
